/**
 * InvestorPreviewContext — "view as investor" mode for Investor Relations.
 *
 * Holds the investorId an Owner is currently previewing. While a preview is
 * active:
 *   - the portal hooks read from ir_previewInvestorPortal instead of the
 *     caller-scoped read callables, so the pages render the INVESTOR's data
 *     rather than the Owner's wider view (see useInvestorPortalPreview);
 *   - /investors renders the investor dashboard instead of redirecting the
 *     admin to the admin dashboard;
 *   - the branded investor portal chrome replaces the employee top bar;
 *   - portal mutations refuse to run — a preview must never activate an
 *     investor, edit their profile, or cast their vote.
 *
 * The id lives in sessionStorage so a refresh does not silently drop the admin
 * back into their own view mid-check, and dies with the tab.
 *
 * Only Owners can preview: the callable requires the Owner role, and this
 * provider clears any stored id for a non-Owner rather than leaving a mode the
 * server will reject.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'officehub.investorPreview.investorId';

interface InvestorPreviewValue {
  /** The investor being previewed, or null when the admin is in their own view. */
  readonly previewInvestorId: string | null;
  readonly isPreviewing: boolean;
  readonly startPreview: (investorId: string) => void;
  readonly stopPreview: () => void;
}

const InvestorPreviewContext = createContext<InvestorPreviewValue>({
  previewInvestorId: null,
  isPreviewing: false,
  startPreview: () => undefined,
  stopPreview: () => undefined,
});

function readStored(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Private browsing / storage disabled — preview still works, it just does
    // not survive a refresh.
    return null;
  }
}

function writeStored(investorId: string | null): void {
  try {
    if (investorId) {
      window.sessionStorage.setItem(STORAGE_KEY, investorId);
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore — see readStored.
  }
}

export function InvestorPreviewProvider({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const isOwner = user?.role === 'Owner';
  // `user` is null while auth resolves on every page load. Treating that as
  // "not an Owner" would wipe the stored id on every refresh — the exact case
  // sessionStorage exists to survive.
  const authResolved = !loading && !!user;
  const [previewInvestorId, setPreviewInvestorId] = useState<string | null>(readStored);

  // A non-Owner can never hold a preview: the callable would reject it, and a
  // half-active mode would render the portal chrome with no data behind it.
  useEffect(() => {
    if (authResolved && !isOwner && previewInvestorId) {
      setPreviewInvestorId(null);
      writeStored(null);
    }
  }, [authResolved, isOwner, previewInvestorId]);

  // Signing out ends the preview. Without this the id outlives the session:
  // the next sign-in on this tab starts inside a stale preview until their
  // auth resolves, and the portal's idle timeout stays suppressed meanwhile.
  const hadUser = useRef(false);
  useEffect(() => {
    if (authResolved) {
      hadUser.current = true;
      return;
    }
    if (hadUser.current && !loading && !user) {
      hadUser.current = false;
      setPreviewInvestorId(null);
      writeStored(null);
    }
  }, [authResolved, loading, user]);

  const startPreview = useCallback(
    (investorId: string) => {
      if (!investorId) return;
      setPreviewInvestorId(investorId);
      writeStored(investorId);
    },
    []
  );

  const stopPreview = useCallback(() => {
    setPreviewInvestorId(null);
    writeStored(null);
  }, []);

  // Before auth resolves, keep the stored id rather than flickering the admin
  // back into their own view; the effect above clears it if they turn out not
  // to be an Owner, and the callable rejects a non-Owner regardless.
  const effectiveId = !authResolved || isOwner ? previewInvestorId : null;

  const value = useMemo<InvestorPreviewValue>(
    () => ({
      previewInvestorId: effectiveId,
      isPreviewing: !!effectiveId,
      startPreview,
      stopPreview,
    }),
    [effectiveId, startPreview, stopPreview]
  );

  return (
    <InvestorPreviewContext.Provider value={value}>
      {children}
    </InvestorPreviewContext.Provider>
  );
}

export function useInvestorPreview(): InvestorPreviewValue {
  return useContext(InvestorPreviewContext);
}
